import { useEffect, useId, useRef, useState } from 'react';
import { toCsvParts } from '@kawai/shared';
import { Button, Select, ZebraTable } from '@kawai/ui';
import { useAuth } from '../lib/auth.js';
import { useTenants } from '../lib/hooks/useTenants.js';
import {
  callTenantExport,
  fetchCollectionAllPages,
  isExportAbortError,
  tenantExportErrorMessage,
  type TenantExportResponse,
} from '../lib/api/exportTenantData.js';
import { downloadTextFile } from '../lib/download.js';
import { CARD, CARD_TITLE, MONO, ROW_BTN_BRAND } from '../lib/uiClasses.js';
import { PageHeader } from '../components/PageHeader.js';
import { Notice } from '../components/Notice.js';

/**
 * S-57: 利用者データエクスポート（契約終了時のデータ救済 / 本体第17条2項）。
 *
 * saas-admin が対象テナントを指定して bookings / users / customers / audit_logs を
 * 取得し、JSON 一括 or コレクション別 CSV でダウンロードする。tenantId スコープは
 * Functions 側（`tenantExportData`）で強制される。
 * S-137: 見た目は docs/UI-GUIDE.md の v2（白カード 30/26・ZebraTable・注意は warning の面）。
 */
export function ExportPage() {
  const { email, signOut } = useAuth();
  const tenantIdInputId = useId();
  const tenantsQuery = useTenants();
  const tenants = tenantsQuery.data ?? [];

  const [tenantId, setTenantId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TenantExportResponse | null>(null);
  const [csvLoadingCol, setCsvLoadingCol] = useState<string | null>(null);
  // S-92 ①: 中断要求済み（キャンセル押下 / unmount）。押下直後のフィードバックに使う。
  const [canceling, setCanceling] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  // S-92 ①: 画面遷移 / signOut で unmount した後もページングが走り続けると、無関係な画面で
  // 突然ダウンロードが発火する（最悪 10,000 ページ × リトライ待機）。unmount で必ず中断する。
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  async function handleFetch() {
    const tid = tenantId.trim();
    if (!tid) {
      setError('テナントを選択してください');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await callTenantExport({ tenantId: tid });
      setResult(res);
    } catch (err) {
      setError(tenantExportErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  function safeStamp(iso: string): string {
    return iso.replace(/[:+]/g, '-');
  }

  function handleDownloadJson() {
    if (!result) return;
    downloadTextFile(
      `export_${result.tenantId}_${safeStamp(result.exportedAt)}.json`,
      JSON.stringify(result.collections, null, 2),
      'application/json',
    );
  }

  /** S-92 ①: 実行中の CSV 全件取得を中断する（gate 中のみ表示されるボタンから呼ぶ）。 */
  function handleCancelCsv() {
    setCanceling(true);
    abortRef.current?.abort();
  }

  async function handleDownloadCsv(col: string) {
    if (!result) return;
    // S-88 review: toCsvParts / Blob 生成の同期例外（巨大データの allocation failure 等）も
    // setError に載せるため download まで try に含め、gate（csvLoadingCol）の解除は
    // finally で構造的に保証する（解除漏れ = 全ボタン恒久 disabled を防ぐ）。
    // S-92: gate をすり抜けた二重起動でも「中断できない実行」を残さないよう、
    // 直前の実行が残っていれば必ず中断してから差し替える。
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setCsvLoadingCol(col);
    setCanceling(false);
    setError(null);
    try {
      let rows = result.collections[col] ?? [];
      // S-92 ③: 非ページングの CSV は初回全件モードのスナップショットなので初回 exportedAt が正。
      let stamp = result.exportedAt;
      // S-84: 全件モードで打ち切られたコレクションはページングで全件取得してから CSV 化する。
      if (result.partial?.[col]?.hasMore) {
        const paged = await fetchCollectionAllPages({
          tenantId: result.tenantId,
          collection: col,
          signal: controller.signal,
        });
        rows = paged.rows;
        // S-92 ③: ページング CSV は押下時点の live データ。初回 exportedAt をファイル名に
        // 使うと取得時点を偽るため、最終ページのサーバ時刻に差し替える。
        stamp = paged.exportedAt;
      }
      downloadTextFile(
        `export_${result.tenantId}_${col}_${safeStamp(stamp)}.csv`,
        // S-88: 巨大 CSV は単一文字列を組み立てず Blob parts でメモリを抑える
        toCsvParts(rows),
        'text/csv',
        { bom: true }, // Excel の Shift_JIS 誤認による文字化けを防ぐ
      );
    } catch (err) {
      // S-92 ①: 中断はユーザー操作 / 画面離脱であって失敗ではないためエラー表示しない。
      if (!isExportAbortError(err)) setError(tenantExportErrorMessage(err));
    } finally {
      // S-92: 自分が設置した controller のときだけ片付ける。無条件に null にすると、
      // 実行が重なったときに後発の controller を消して中断ボタン / unmount abort が
      // 何も止められなくなる。
      if (abortRef.current === controller) abortRef.current = null;
      setCsvLoadingCol(null);
      setCanceling(false);
    }
  }

  const jsonBlocked = result?.truncated || csvLoadingCol !== null;

  return (
    <main className="mx-auto max-w-[960px] p-6">
      <PageHeader
        title="データエクスポート"
        description="契約終了時のデータ救済（第17条2項 / S-57）。テナント単位で CSV / JSON 取得。"
        email={email}
        links={[{ to: '/', label: '← テナント一覧' }]}
        onSignOut={() => {
          void signOut();
        }}
      />

      <section className={`mt-6 ${CARD}`} aria-label="エクスポート対象">
        <h2 className={CARD_TITLE}>エクスポート対象</h2>
        <div className="mt-4 flex flex-col gap-1.5">
          <label htmlFor={tenantIdInputId} className="text-label font-medium text-ink-2">
            テナント
          </label>
          <Select
            id={tenantIdInputId}
            className="max-w-[360px]"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            disabled={tenantsQuery.isLoading || tenantsQuery.isError}
          >
            <option value="">
              {tenantsQuery.isLoading
                ? '読み込み中…'
                : tenantsQuery.isError
                  ? 'テナント取得に失敗しました'
                  : '— テナントを選択 —'}
            </option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.shopName}（{t.id}）{t.status !== 'active' ? ` [${t.status}]` : ''}
              </option>
            ))}
          </Select>
        </div>
        <div className="mt-4">
          <Button
            type="button"
            onClick={() => {
              void handleFetch();
            }}
            // S-88: CSV 全件取得中の再取得は result を差し替え競合するため gate する
            disabled={loading || !tenantId || csvLoadingCol !== null}
          >
            {loading ? '取得中…' : 'データ取得'}
          </Button>
        </div>

        {error && (
          <Notice role="alert" className="mt-4">
            {error}
          </Notice>
        )}
      </section>

      {result && (
        <section className={`mt-6 ${CARD}`} aria-label="エクスポート結果">
          <h2 className={CARD_TITLE}>エクスポート結果</h2>
          {result.truncated && (
            <Notice role="alert" tone="warning" className="mt-4">
              データ量が多いため、一覧の件数と「JSON
              一括ダウンロード」は一部のみです（件数の「+」付きと「未取得」が該当）。各コレクションの「CSV
              ダウンロード」は全件を自動でページ取得します（大規模時は時間がかかります）。一括取得は
              CLI エクスポート（RUNBOOK §6.3c）も利用できます。
              {/* S-92 ③: ページング CSV は初回取得のスナップショットではない旨を明示する */}
              ページ取得した CSV
              は押下時点のデータで、ファイル名の時刻は取得完了時点になります。
            </Notice>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-4">
            <span className="text-body text-ink-2">
              tenantId:{' '}
              <code className={`${MONO} rounded-lg bg-surface-2 px-2 py-0.5 text-ink`}>
                {result.tenantId}
              </code>
            </span>
            <span className={`${MONO} text-ink-3`}>取得時刻: {result.exportedAt}</span>
            <Button
              type="button"
              size="sm"
              onClick={handleDownloadJson}
              disabled={jsonBlocked}
              title={
                result.truncated
                  ? '大規模のため JSON 一括は不完全になります。各コレクションの CSV ダウンロードをご利用ください'
                  : undefined
              }
            >
              {result.truncated
                ? 'JSON 一括（大規模のため不可）'
                : 'JSON 一括ダウンロード'}
            </Button>
            {/* S-92 ①: 全件取得は数分〜に及ぶため、gate 中は中断手段を必ず出す */}
            {csvLoadingCol !== null && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleCancelCsv}
                disabled={canceling}
              >
                {canceling ? '中断中…' : '取得を中断'}
              </Button>
            )}
            {/* 発行済みリクエスト 1 本は中断できない（httpsCallable が signal 非対応）ため、
                押下後しばらく画面が固まったように見える。理由を出して無反応と誤解させない。 */}
            {canceling && (
              <span className="text-label text-ink-3">
                現在のページの応答を待っています（最大 1 ページ分）
              </span>
            )}
          </div>
          <div className="mt-4 overflow-x-auto">
            <ZebraTable aria-label="コレクション別の件数">
              <thead>
                <ZebraTable.HeadRow>
                  <ZebraTable.Th edge="first">コレクション</ZebraTable.Th>
                  <ZebraTable.Th>件数</ZebraTable.Th>
                  <ZebraTable.Th edge="last">CSV</ZebraTable.Th>
                </ZebraTable.HeadRow>
              </thead>
              <tbody>
                {Object.keys(result.counts).map((col) => {
                  const info = result.partial?.[col];
                  const hasMore = info?.hasMore ?? false;
                  // S-88: unread は予算枯渇で読取自体が未実施（0 件確定ではない）
                  const isUnread = info?.reason === 'unread';
                  const isEmpty = result.counts[col] === 0 && !hasMore;
                  return (
                    <ZebraTable.Row key={col}>
                      <ZebraTable.Td className={MONO}>{col}</ZebraTable.Td>
                      <ZebraTable.Td>
                        {isUnread ? (
                          <span title="データ量超過のため件数未確定です（CSV ダウンロードで全件取得します）">
                            未取得
                          </span>
                        ) : (
                          `${result.counts[col]}${hasMore ? '+' : ''}`
                        )}
                      </ZebraTable.Td>
                      <ZebraTable.Td>
                        <button
                          type="button"
                          onClick={() => {
                            void handleDownloadCsv(col);
                          }}
                          disabled={isEmpty || csvLoadingCol !== null}
                          className={ROW_BTN_BRAND}
                        >
                          {csvLoadingCol === col ? '取得中…' : 'CSV ダウンロード'}
                        </button>
                      </ZebraTable.Td>
                    </ZebraTable.Row>
                  );
                })}
              </tbody>
            </ZebraTable>
          </div>
        </section>
      )}
    </main>
  );
}
